import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { MediaType } from "@/types";
import { useSeo } from "@/hooks/useSeo";
import {
  filterTitles,
  getGenres,
  getRegions,
  getTitlesByType,
  getYears,
  sortOptions,
  sortTitles,
  typeMetaMap,
  type SortKey,
} from "@/lib/titles";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TitleCard from "@/components/feature/TitleCard";
import Pagination from "@/components/feature/Pagination";
import Reveal from "@/components/base/Reveal";
import FilterPanel, { type FilterState } from "@/pages/category/components/FilterPanel";

const PAGE_SIZE = 6;
const DEFAULT_FILTER: FilterState = { region: "全部", year: "全部", genre: "全部", minRating: 0 };
const VALID_TYPES: MediaType[] = ["movie", "tv", "variety"];

function isMediaType(value?: string): value is MediaType {
  return !!value && VALID_TYPES.includes(value as MediaType);
}

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const valid = isMediaType(type);
  const meta = valid ? typeMetaMap[type] : null;

  const [filter, setFilter] = useState<FilterState>(DEFAULT_FILTER);
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);

  useSeo({
    title: meta ? `${meta.label} - WW我的快乐在哪里影视资讯` : "页面不存在 - WW我的快乐在哪里",
    description: meta
      ? `${meta.description} 立即浏览 WW我的快乐在哪里 为你整理的${meta.label}资讯与剧情介绍。`
      : "抱歉，未找到对应的影视分类页面。",
    keywords: meta
      ? `${meta.label},${meta.label}推荐,${meta.label}剧情介绍,WW我的快乐在哪里,影视资讯`
      : "WW我的快乐在哪里",
    path: `https://db6pnwez.cn/category/${type ?? ""}`,
  });

  const base = useMemo(() => (meta ? getTitlesByType(meta.en) : []), [meta]);
  const regions = useMemo(() => (meta ? getRegions(meta.en) : []), [meta]);
  const years = useMemo(() => (meta ? getYears(meta.en) : []), [meta]);
  const genres = useMemo(() => (meta ? getGenres(meta.en) : []), [meta]);

  const results = useMemo(
    () => sortTitles(filterTitles(base, filter), sort),
    [base, filter, sort]
  );

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedItems = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const updateFilter = (patch: Partial<FilterState>) => {
    setFilter((prev) => ({ ...prev, ...patch }));
    setPage(1);
  };

  const reset = () => {
    setFilter(DEFAULT_FILTER);
    setSort("latest");
    setPage(1);
  };

  if (!meta) {
    return (
      <section className="mx-auto flex w-full max-w-page flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-700">
          <i className="ri-emotion-sad-line text-3xl" aria-hidden="true" />
        </span>
        <h1 className="mt-6 text-2xl font-bold text-foreground-950">该分类不存在</h1>
        <p className="mt-3 text-sm text-foreground-600">
          你访问的分类暂时没有内容，可以返回首页或浏览其他分类。
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700 whitespace-nowrap"
          >
            <i className="ri-home-4-line" aria-hidden="true" />
            返回首页
          </Link>
          <Link
            to="/category/movie"
            className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 whitespace-nowrap"
          >
            浏览电影
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto w-full max-w-page px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: meta.label }]} />

      <header className="mt-5">
        <h1 className="text-2xl font-black text-foreground-950 md:text-3xl">
          {meta.label}资讯与剧情介绍
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
          {meta.description}
        </p>
      </header>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="mb-5 flex items-center gap-2 text-base font-bold text-foreground-950">
              <i className="ri-filter-3-line text-primary-600" aria-hidden="true" />
              筛选条件
            </h2>
            <FilterPanel
              type={meta.en}
              value={filter}
              regions={regions}
              years={years}
              genres={genres}
              onChange={updateFilter}
              onReset={reset}
            />
          </div>
        </aside>

        <div>
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
            <p className="text-sm text-foreground-600">
              共找到 <span className="font-bold text-primary-600">{results.length}</span> 部{meta.label}作品
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setPanelOpen((open) => !open)}
                className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 lg:hidden"
              >
                <i className="ri-filter-3-line" aria-hidden="true" />
                筛选
              </button>
              <span className="hidden text-xs text-foreground-500 sm:inline">排序</span>
              <div className="flex flex-wrap gap-1.5">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      setSort(option.key);
                      setPage(1);
                    }}
                    className={`rounded-md border px-3 py-1.5 text-sm transition-colors whitespace-nowrap ${
                      sort === option.key
                        ? "border-secondary-500 bg-secondary-500 font-medium text-background-50"
                        : "border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {panelOpen && (
            <div className="mb-5 rounded-lg border border-background-200 bg-background-50 p-5 lg:hidden">
              <FilterPanel
                type={meta.en}
                value={filter}
                regions={regions}
                years={years}
                genres={genres}
                onChange={updateFilter}
                onReset={reset}
              />
            </div>
          )}

          {pagedItems.length > 0 ? (
            <>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {pagedItems.map((item, index) => (
                  <Reveal key={item.id} delay={index * 50}>
                    <TitleCard item={item} className="h-full" />
                  </Reveal>
                ))}
              </div>
              <Pagination totalPages={totalPages} current={currentPage} onChange={setPage} />
            </>
          ) : (
            <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                <i className="ri-inbox-2-line text-3xl" aria-hidden="true" />
              </span>
              <h2 className="mt-5 text-lg font-bold text-foreground-950">暂无符合条件的影视作品</h2>
              <p className="mt-2 text-sm text-foreground-600">试着放宽筛选条件，或直接重置查看全部内容。</p>
              <button
                type="button"
                onClick={reset}
                className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700 whitespace-nowrap"
              >
                <i className="ri-refresh-line" aria-hidden="true" />
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}